/**
 * Phase 3 — Suggested Signals (Draft) Section
 * 
 * Renders a collapsible gold-bordered section above the live signals on the Market Intelligence page.
 * Strict rules:
 *   - Nothing here is "live" until the user clicks Confirm
 *   - Confirm → existing market_signals collection (server-side)
 *   - Reject  → rejected_drafts (audit trail)
 *   - Edit    → updates draft only; does not touch market_signals
 *   - Existing signals & sweep logic untouched (this is a parallel writer)
 */
import { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import {
  Sparkles,
  ChevronDown,
  ChevronUp,
  CheckCircle2,
  XCircle,
  Pencil,
  AlertCircle,
  Compass,
  Target,
  Package,
  ExternalLink,
  Loader2,
  Calendar,
  Building2,
  Layers3,
  Zap,
} from 'lucide-react';
import { useApi } from '../../hooks/useApi';
import { toast } from 'sonner';

const PRIORITY_STYLES = {
  high: 'bg-red-100 text-red-700 border-red-300',
  medium: 'bg-amber-100 text-amber-800 border-amber-300',
  low: 'bg-slate-100 text-slate-600 border-slate-300',
};

const MODE_ICON = {
  company: Building2,
  events: Calendar,
  hybrid: Layers3,
};

export function SuggestedSignalsDraftSection({ onConfirmedSignal }) {
  const api = useApi();
  const apiRef = useRef(api);
  apiRef.current = api;
  const [open, setOpen] = useState(true);
  const [loading, setLoading] = useState(true);
  const [drafts, setDrafts] = useState([]);
  const [detectionMode, setDetectionMode] = useState('company');
  const [isSample, setIsSample] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [editingDraft, setEditingDraft] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [generating, setGenerating] = useState(false);

  const loadDrafts = async () => {
    setLoading(true);
    try {
      const res = await apiRef.current.get('/signal-drafts/list');
      setDrafts(res.drafts || []);
      setDetectionMode(res.detection_mode || 'company');
      setIsSample(!!res.is_sample);
    } catch (e) {
      console.warn('Could not load draft signals:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateNow = async (e) => {
    e?.stopPropagation();
    setGenerating(true);
    try {
      // Feb 2026 — async fire-and-poll flow. The endpoint returns
      // immediately with { run_id, status: "running" }. We poll the
      // status URL every 5s until it flips to completed / failed /
      // stalled, then refresh the drafts list.
      const kickoff = await apiRef.current.post('/signal-drafts/generate-now');
      const runId = kickoff?.run_id;
      const pollInterval = (kickoff?.poll_interval_seconds || 5) * 1000;
      const maxAttempts = 180; // ≈ 15 min ceiling

      if (!runId) {
        // Legacy sync response — treat kickoff payload as the summary.
        const count = kickoff?.generated || 0;
        if (count > 0) {
          toast.success(`${count} new signal${count === 1 ? '' : 's'} found`);
          await loadDrafts();
        } else {
          toast.info(kickoff?.message || 'No new signals found — try again later');
        }
        return;
      }

      toast.info('Sweep started — this can take a few minutes...');
      let attempt = 0;
      // eslint-disable-next-line no-constant-condition
      while (true) {
        if (attempt++ >= maxAttempts) {
          toast.warning('Sweep is taking longer than expected. Check back shortly.');
          break;
        }
        await new Promise(res => setTimeout(res, pollInterval));
        const status = await apiRef.current.get(`/signal-drafts/generate-status/${runId}`);
        if (status?.status === 'completed') {
          const count = status?.summary?.generated || 0;
          if (count > 0) {
            toast.success(`${count} new signal${count === 1 ? '' : 's'} found`);
          } else {
            toast.info(status?.summary?.message || 'No new signals found — try again later');
          }
          await loadDrafts();
          break;
        }
        if (status?.status === 'failed' || status?.status === 'stalled') {
          toast.error(status?.error || `Sweep ${status.status}`);
          break;
        }
      }
    } catch (err) {
      toast.error(err.message || 'Generate failed');
    } finally {
      setGenerating(false);
    }
  };

  useEffect(() => {
    loadDrafts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleConfirm = async (draft) => {
    setBusyId(draft.id);
    try {
      const res = await api.post(`/signal-drafts/${draft.id}/confirm`);
      toast.success(`Signal confirmed and promoted to live: "${draft.title.slice(0, 60)}..."`);
      setDrafts(prev => prev.filter(d => d.id !== draft.id));
      onConfirmedSignal?.(res.promoted_signal);
    } catch (e) {
      toast.error(e.message || 'Failed to confirm draft');
    } finally {
      setBusyId(null);
    }
  };

  const handleReject = async (draft) => {
    if (!confirm(`Reject this draft signal?\n\n"${draft.title}"\n\nIt will be moved to the audit trail and never promoted.`)) return;
    setBusyId(draft.id);
    try {
      await api.post(`/signal-drafts/${draft.id}/reject`);
      toast.info('Draft rejected and moved to audit trail');
      setDrafts(prev => prev.filter(d => d.id !== draft.id));
    } catch (e) {
      toast.error(e.message || 'Failed to reject draft');
    } finally {
      setBusyId(null);
    }
  };

  const openEdit = (draft) => {
    setEditingDraft(draft);
    setEditForm({
      title: draft.title || '',
      snippet: draft.snippet || '',
      why_matched: draft.why_matched || '',
      priority: draft.priority || 'medium',
      product_match_name: draft.product_match?.product_name || '',
      product_match_reason: draft.product_match?.match_reason || '',
      dm_title: draft.decision_maker?.title || '',
      dm_name: draft.decision_maker?.name || '',
      dm_organization: draft.decision_maker?.organization || '',
      dm_why: draft.decision_maker?.why_this_role || '',
    });
  };

  const saveEdit = async () => {
    if (!editingDraft) return;
    setBusyId(editingDraft.id);
    try {
      const body = {
        title: editForm.title,
        snippet: editForm.snippet,
        why_matched: editForm.why_matched,
        priority: editForm.priority,
        product_match: editForm.product_match_name ? {
          product_name: editForm.product_match_name,
          match_reason: editForm.product_match_reason || '',
        } : null,
        decision_maker: editForm.dm_title ? {
          title: editForm.dm_title,
          name: editForm.dm_name || null,
          organization: editForm.dm_organization || null,
          why_this_role: editForm.dm_why || null,
        } : null,
      };
      const res = await api.put(`/signal-drafts/${editingDraft.id}/edit`, body);
      toast.success('Draft updated (still draft — Confirm to promote to live)');
      setEditingDraft(null);
      // Replace draft in list with updated version
      const newId = res.draft?.id || editingDraft.id;
      setDrafts(prev => prev.map(d => d.id === editingDraft.id ? { ...d, ...res.draft, id: newId } : d));
    } catch (e) {
      toast.error(e.message || 'Failed to save edit');
    } finally {
      setBusyId(null);
    }
  };

  const ModeIcon = MODE_ICON[detectionMode] || Compass;
  const draftCount = drafts.length;

  if (loading) {
    return (
      <Card className="border-2 border-[#B89B5E]/40 bg-[#B89B5E]/5">
        <CardContent className="py-6 flex items-center gap-3 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading suggested signals...
        </CardContent>
      </Card>
    );
  }

  if (draftCount === 0 && !isSample) {
    // Show a tiny "empty state" card with Generate Now so users always
    // have a way to trigger a fresh sweep. Previously this returned null,
    // which hid the Generate Now button entirely once drafts hit zero.
    return (
      <Card
        className="border border-[#B89B5E]/30 bg-white"
        data-testid="suggested-signals-empty-state"
      >
        <CardContent className="py-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div>
            <p className="text-sm font-medium text-[#1C1C1C]">No suggested signals right now</p>
            <p className="text-xs text-muted-foreground mt-1">
              Run a fresh sweep to scan your market for new signals.
            </p>
          </div>
          <Button
            data-testid="generate-now-btn-empty"
            onClick={handleGenerateNow}
            disabled={generating}
            className="bg-[#B89B5E] hover:bg-[#9c8350] text-white whitespace-nowrap"
          >
            {generating ? (
              <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Generating…</>
            ) : (
              <><Zap className="h-4 w-4 mr-1" /> Generate Now</>
            )}
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card
        className="border-2 border-[#B89B5E]/60 bg-gradient-to-r from-[#B89B5E]/5 to-transparent shadow-sm"
        data-testid="suggested-signals-draft-section"
        id="suggested-signals-draft"
      >
        <CardHeader
          className="cursor-pointer pb-3"
          onClick={() => setOpen(!open)}
        >
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#B89B5E]/15 flex items-center justify-center">
                <Sparkles className="h-5 w-5 text-[#B89B5E]" />
              </div>
              <div>
                <CardTitle className="text-base flex items-center gap-2 flex-wrap">
                  Suggested Signals (Draft) — {draftCount} awaiting review
                  <Badge variant="outline" className="text-xs border-[#B89B5E]/40 text-[#B89B5E]">
                    <ModeIcon className="w-3 h-3 mr-1" /> {detectionMode.toUpperCase()} mode
                  </Badge>
                  {isSample && (
                    <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">
                      Sample preview
                    </Badge>
                  )}
                </CardTitle>
                <p className="text-xs text-muted-foreground mt-1">
                  Generated by Industry Architect engine. Confirm to promote to live signals. Existing signals untouched.
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                onClick={handleGenerateNow}
                disabled={generating}
                className="bg-[#B89B5E] hover:bg-[#A08050] text-white"
                data-testid="generate-now-btn"
              >
                {generating ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-1 animate-spin" /> Generating signals...
                  </>
                ) : (
                  <>
                    <Zap className="h-4 w-4 mr-1" /> Generate Now
                  </>
                )}
              </Button>
              <Button variant="ghost" size="sm" data-testid="toggle-drafts-section" onClick={(e) => { e.stopPropagation(); setOpen(!open); }}>
                {open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              </Button>
            </div>
          </div>
        </CardHeader>

        {open && (
          <CardContent className="space-y-3">
            {detectionMode === 'hybrid' ? (
              <>
                {/* Hybrid mode — two clearly labeled streams */}
                <HybridStream
                  label="Company Stream"
                  icon={Building2}
                  drafts={drafts.filter(d => (d.stream || d.detection_mode) === 'company')}
                  busyId={busyId}
                  onConfirm={handleConfirm}
                  onReject={handleReject}
                  onEdit={openEdit}
                />
                <HybridStream
                  label="Events Stream"
                  icon={Calendar}
                  drafts={drafts.filter(d => (d.stream || d.detection_mode) === 'events')}
                  busyId={busyId}
                  onConfirm={handleConfirm}
                  onReject={handleReject}
                  onEdit={openEdit}
                />
              </>
            ) : (
              drafts.map((d) => (
                <DraftSignalCard
                  key={d.id}
                  draft={d}
                  busy={busyId === d.id}
                  onConfirm={() => handleConfirm(d)}
                  onReject={() => handleReject(d)}
                  onEdit={() => openEdit(d)}
                />
              ))
            )}
          </CardContent>
        )}
      </Card>

      {/* Edit modal */}
      <Dialog open={!!editingDraft} onOpenChange={(o) => !o && setEditingDraft(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="h-4 w-4" /> Edit Draft Signal
              <Badge className="bg-amber-100 text-amber-800 border-amber-300 ml-2">DRAFT</Badge>
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div>
              <Label className="text-xs">Title</Label>
              <Input
                value={editForm.title || ''}
                onChange={(e) => setEditForm(f => ({ ...f, title: e.target.value }))}
                data-testid="edit-draft-title"
              />
            </div>
            <div>
              <Label className="text-xs">Snippet</Label>
              <Textarea
                rows={3}
                value={editForm.snippet || ''}
                onChange={(e) => setEditForm(f => ({ ...f, snippet: e.target.value }))}
              />
            </div>
            <div>
              <Label className="text-xs">Why this matched</Label>
              <Textarea
                rows={2}
                value={editForm.why_matched || ''}
                onChange={(e) => setEditForm(f => ({ ...f, why_matched: e.target.value }))}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Priority</Label>
                <select
                  value={editForm.priority || 'medium'}
                  onChange={(e) => setEditForm(f => ({ ...f, priority: e.target.value }))}
                  className="w-full border rounded-md px-3 py-2 text-sm"
                >
                  <option value="high">High</option>
                  <option value="medium">Medium</option>
                  <option value="low">Low</option>
                </select>
              </div>
            </div>

            <div className="border-t pt-3">
              <p className="text-xs font-medium uppercase tracking-wide text-stone-500 mb-2">Product match</p>
              <div className="grid grid-cols-1 gap-2">
                <Input
                  placeholder="Product name"
                  value={editForm.product_match_name || ''}
                  onChange={(e) => setEditForm(f => ({ ...f, product_match_name: e.target.value }))}
                />
                <Textarea
                  rows={2}
                  placeholder="Why this product fits the signal"
                  value={editForm.product_match_reason || ''}
                  onChange={(e) => setEditForm(f => ({ ...f, product_match_reason: e.target.value }))}
                />
              </div>
            </div>

            <div className="border-t pt-3">
              <p className="text-xs font-medium uppercase tracking-wide text-stone-500 mb-2">Decision maker to target</p>
              <div className="grid grid-cols-2 gap-2">
                <Input
                  placeholder="Title (e.g. Sponsorship Manager)"
                  value={editForm.dm_title || ''}
                  onChange={(e) => setEditForm(f => ({ ...f, dm_title: e.target.value }))}
                  data-testid="edit-draft-dm-title"
                />
                <Input
                  placeholder="Name (optional)"
                  value={editForm.dm_name || ''}
                  onChange={(e) => setEditForm(f => ({ ...f, dm_name: e.target.value }))}
                />
              </div>
              <Input
                placeholder="Organization"
                className="mt-2"
                value={editForm.dm_organization || ''}
                onChange={(e) => setEditForm(f => ({ ...f, dm_organization: e.target.value }))}
              />
              <Textarea
                rows={2}
                placeholder="Why this role"
                className="mt-2"
                value={editForm.dm_why || ''}
                onChange={(e) => setEditForm(f => ({ ...f, dm_why: e.target.value }))}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingDraft(null)}>Cancel</Button>
            <Button onClick={saveEdit} disabled={busyId === editingDraft?.id} className="bg-[#B89B5E] hover:bg-[#A08050]" data-testid="save-edit-draft-btn">
              {busyId === editingDraft?.id ? 'Saving...' : 'Save Edits (still Draft)'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}


function HybridStream({ label, icon: Icon, drafts, busyId, onConfirm, onReject, onEdit }) {
  return (
    <div className="space-y-2" data-testid={`hybrid-stream-${label.toLowerCase().replace(' ', '-')}`}>
      <div className="flex items-center gap-2 px-2 py-1.5 bg-stone-100 rounded-md border border-stone-200">
        <Icon className="h-4 w-4 text-stone-600" />
        <h4 className="text-sm font-semibold text-stone-700">{label}</h4>
        <Badge variant="outline" className="ml-auto text-xs">{drafts.length}</Badge>
      </div>
      {drafts.length === 0 ? (
        <p className="text-xs text-stone-500 italic px-3 py-2">No drafts in this stream yet</p>
      ) : (
        drafts.map((d) => (
          <DraftSignalCard
            key={d.id}
            draft={d}
            busy={busyId === d.id}
            onConfirm={() => onConfirm(d)}
            onReject={() => onReject(d)}
            onEdit={() => onEdit(d)}
          />
        ))
      )}
    </div>
  );
}


function DraftSignalCard({ draft, busy, onConfirm, onReject, onEdit }) {
  const dm = draft.decision_maker;
  const product = draft.product_match;
  const priorityClass = PRIORITY_STYLES[draft.priority] || PRIORITY_STYLES.medium;

  return (
    <Card className="border border-stone-200 hover:border-[#B89B5E]/50 transition-colors" data-testid={`draft-signal-${draft.id}`}>
      <CardContent className="pt-4 pb-3">
        <div className="flex items-start justify-between gap-3 mb-2 flex-wrap">
          <div className="flex items-center gap-2 flex-wrap">
            <Badge className="bg-amber-100 text-amber-800 border-amber-300" data-testid="draft-badge">
              <AlertCircle className="w-3 h-3 mr-1" /> DRAFT
            </Badge>
            <Badge variant="outline" className={priorityClass}>
              {draft.priority?.toUpperCase()} PRIORITY
            </Badge>
            {draft.detection_mode && (
              <Badge variant="outline" className="text-xs">
                {draft.detection_mode.toUpperCase()} mode
              </Badge>
            )}
          </div>
          {draft.source_url && (
            <a
              href={draft.source_url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-[#B89B5E] hover:underline flex items-center gap-1"
              onClick={(e) => e.stopPropagation()}
            >
              View source <ExternalLink className="w-3 h-3" />
            </a>
          )}
        </div>

        <h4 className="font-semibold text-base text-stone-900">{draft.title}</h4>
        <p className="text-sm text-stone-600 mt-1">{draft.snippet}</p>

        {/* Why this matched */}
        <div className="mt-3 p-3 bg-stone-50 rounded-md border border-stone-200">
          <p className="text-xs font-medium uppercase tracking-wide text-[#B89B5E] mb-1 flex items-center gap-1">
            <Sparkles className="w-3 h-3" /> Why this matched
          </p>
          <p className="text-sm text-stone-700">{draft.why_matched}</p>
        </div>

        {/* Product + DM */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
          {product && (
            <div className="p-3 rounded-md bg-white border border-stone-200" data-testid="product-match-block">
              <p className="text-xs font-medium uppercase tracking-wide text-stone-500 mb-1 flex items-center gap-1">
                <Package className="w-3 h-3" /> Product match
              </p>
              <p className="text-sm font-medium text-stone-800">{product.product_name}</p>
              <p className="text-xs text-stone-600 mt-1">{product.match_reason}</p>
            </div>
          )}
          {dm && (
            <div className="p-3 rounded-md bg-white border border-stone-200" data-testid="decision-maker-block">
              <p className="text-xs font-medium uppercase tracking-wide text-stone-500 mb-1 flex items-center gap-1">
                <Target className="w-3 h-3" /> Decision maker to target
              </p>
              <p className="text-sm font-medium text-stone-800">
                {dm.title}{dm.name ? ` — ${dm.name}` : ''}
              </p>
              {dm.organization && <p className="text-xs text-stone-600">{dm.organization}</p>}
              {dm.why_this_role && <p className="text-xs text-stone-500 mt-1">{dm.why_this_role}</p>}
            </div>
          )}
        </div>

        {/* Action buttons */}
        <div className="flex items-center gap-2 mt-3 flex-wrap">
          <Button
            size="sm"
            onClick={onConfirm}
            disabled={busy}
            className="bg-green-600 hover:bg-green-700 text-white"
            data-testid="confirm-draft-btn"
          >
            <CheckCircle2 className="h-4 w-4 mr-1" /> Confirm
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={onEdit}
            disabled={busy}
            data-testid="edit-draft-btn"
          >
            <Pencil className="h-4 w-4 mr-1" /> Edit
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={onReject}
            disabled={busy}
            className="text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
            data-testid="reject-draft-btn"
          >
            <XCircle className="h-4 w-4 mr-1" /> Reject
          </Button>
          {busy && <Loader2 className="h-4 w-4 animate-spin text-stone-400" />}
        </div>

        {/* Filter decisions disclosure (Phase 4 transparency) */}
        {Array.isArray(draft.filter_decisions) && draft.filter_decisions.length > 0 && (
          <details className="mt-3 group" data-testid="filter-decisions-disclosure">
            <summary className="text-xs cursor-pointer text-stone-500 hover:text-stone-700 select-none">
              <span className="inline-flex items-center gap-1">
                <span className="group-open:hidden">Show filter decisions</span>
                <span className="hidden group-open:inline">Hide filter decisions</span>
                {draft.confidence != null && (
                  <span className="ml-2 text-stone-400">
                    confidence {(draft.confidence * 100).toFixed(0)}%
                  </span>
                )}
              </span>
            </summary>
            <div className="mt-2 p-3 rounded-md bg-stone-50 border border-stone-200">
              <p className="text-xs font-medium uppercase tracking-wide text-stone-500 mb-2">Phase 4 Quality Filter — decisions</p>
              <ul className="space-y-1">
                {draft.filter_decisions.map((fd, i) => (
                  <li key={i} className="flex items-start gap-2 text-xs">
                    {fd.passed ? (
                      <CheckCircle2 className="w-3.5 h-3.5 text-green-600 mt-0.5 flex-shrink-0" />
                    ) : (
                      <XCircle className="w-3.5 h-3.5 text-red-500 mt-0.5 flex-shrink-0" />
                    )}
                    <div>
                      <span className="font-medium text-stone-700">{fd.rule}</span>
                      {fd.detail && (
                        <span className="text-stone-500 ml-1">— {fd.detail}</span>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </details>
        )}
      </CardContent>
    </Card>
  );
}
